<vw-header-padding-safe-area></vw-header-padding-safe-area>

<div class="px-4 pb-28">
  <div class="flex items-center gap-2 pb-1">
    <button
      type="button"
      class="-ml-2 flex h-9 w-9 items-center justify-center rounded-full text-[#062825]"
      (click)="goBack()"
      [attr.aria-label]="'common.back' | translate"
    >
      <svg viewBox="0 0 24 24" class="h-6 w-6" fill="none" aria-hidden="true">
        <path d="M15 6l-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>
    <h2 class="text-[28px] font-semibold text-[#062825]">
      {{ 'lunar.title' | translate }}
    </h2>
  </div>

  <!-- Jamais présenté comme une science : c'est une tradition de jardinage. -->
  <p class="pb-4 text-xs text-[#9BA6A1]">
    {{ 'lunar.tradition' | translate }}
  </p>

  @if (isLoading) {
    <vw-skeleton-loader [width]="'100%'" [height]="'96px'" [borderRadius]="'0.75rem'"></vw-skeleton-loader>
  } @else {

  <!-- Bandeau des 7 jours -->
  <div class="-mx-4 overflow-x-auto px-4 pb-1">
    <div class="flex gap-2 w-max">
      @for (day of days; track day.date) {
        <button
          type="button"
          (click)="select(day)"
          class="relative flex w-[62px] shrink-0 flex-col items-center gap-1 rounded-2xl border px-2 py-2.5 transition-colors"
          [class]="
            day.date === selected?.date
              ? 'border-[#7C6BD6] bg-[#EFECFF]'
              : 'border-gray-100 bg-white'
          "
        >
          <span class="text-[11px] uppercase text-[#9BA6A1]">{{ day.weekday }}</span>
          <span class="text-base font-semibold text-[#062825]">{{ day.dayNumber }}</span>

          @if (day.locked) {
            <span class="flex h-6 w-6 items-center justify-center text-[#C9BFA6]">
              <vw-lock-icon class="h-4 w-4" />
            </span>
          } @else if (day.neutral) {
            <span class="text-lg" [attr.aria-label]="'lunar.neutral.badge' | translate">💤</span>
          } @else if (day.influence) {
            <span class="text-lg">{{ 'lunar.influences.' + day.influence + '.emoji' | translate }}</span>
          } @else {
            <span class="text-lg text-[#D5DAD8]">—</span>
          }

          <!-- Montante / descendante : ce que la tradition lit dans la sève -->
          @if (!day.locked && day.moonPath) {
            <span class="text-[10px] text-[#7C6BD6]">
              {{ day.moonPath === 'montante' ? '↑' : '↓' }}
            </span>
          }

          @if (day.isToday) {
            <span class="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-[#7C6BD6]"></span>
          }
        </button>
      }
    </div>
  </div>

  <!-- Détail du jour sélectionné -->
  @if (selected) {
    <div class="mt-4 rounded-2xl bg-white p-4 shadow-sm">
      @if (selected.neutral) {
        <!-- Jour de repos : pas d'action, et c'est le message -->
        <div class="flex items-start gap-3">
          <span class="text-2xl">💤</span>
          <div class="min-w-0">
            <h3 class="text-lg font-semibold text-[#062825]">
              {{ 'lunar.neutral.title' | translate }}
            </h3>
            <p class="mt-1 text-sm leading-relaxed text-[#617069]">
              {{ 'lunar.neutral.text' | translate }}
            </p>
          </div>
        </div>
      } @else if (selected.influence) {
        <div class="flex items-start gap-3">
          <span class="text-2xl">{{ 'lunar.influences.' + selected.influence + '.emoji' | translate }}</span>
          <div class="min-w-0 flex-1">
            <h3 class="text-lg font-semibold text-[#062825]">
              {{ 'lunar.influences.' + selected.influence + '.title' | translate }}
            </h3>
            <p class="mt-0.5 text-sm text-[#617069]">
              {{ 'lunar.influences.' + selected.influence + '.concerns' | translate }}
            </p>
          </div>
        </div>

        <!-- Les gestes : la seule chose qui rende la chip « jour racines »
             utile à qui ne connaît pas la tradition. -->
        <p class="mt-4 text-[11px] font-semibold uppercase tracking-wide text-[#9BA6A1]">
          {{ 'lunar.actions-title' | translate }}
        </p>
        <ul class="mt-2 flex flex-col gap-2">
          @for (i of [0, 1, 2]; track i) {
            <li class="flex items-start gap-2.5">
              <span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#7C6BD6]"></span>
              <span class="text-sm leading-snug text-[#062825]">
                {{ 'lunar.influences.' + selected.influence + '.actions.' + i | translate }}
              </span>
            </li>
          }
        </ul>

        @if (selected.moonPath) {
          <div class="mt-4 rounded-xl bg-[#F6F5FF] px-3 py-2">
            <p class="text-xs leading-relaxed text-[#4C4080]">
              {{ 'lunar.path.' + selected.moonPath | translate }}
            </p>
          </div>
        }

        @if (selected.events.length) {
          <div class="mt-2 flex flex-wrap gap-1.5">
            @for (e of selected.events; track e) {
              <span class="rounded-full bg-[#FFF6E4] px-2 py-0.5 text-[11px] text-[#7A4B00]">
                {{ 'home.lunar.events.' + e | translate }}
              </span>
            }
          </div>
        }
      }
    </div>
  }

  <!-- Ce que ça change pour SON jardin : sans ça, la page reste théorique -->
  @if (selected?.isToday && matchedCount > 0) {
    <div class="mt-3 rounded-2xl border border-[#DCEFE3] bg-[#F2FAF5] px-4 py-3">
      <p class="text-sm text-[#1C5C41]">
        {{ 'lunar.matched' | translate: { count: matchedCount } }}
      </p>
    </div>
  }

  <!-- Volet « à semer », visuellement séparé des cultures en place -->
  @if (selected?.isToday && sowingSuggestions.length) {
    <h3 class="mt-6 text-lg font-semibold text-[#062825]">
      {{ 'lunar.sowing.title' | translate }}
    </h3>
    <p class="text-xs text-[#9BA6A1]">{{ 'lunar.sowing.subtitle' | translate }}</p>
    <div class="mt-2 flex flex-col gap-2">
      @for (s of sowingSuggestions; track s.cultivationUid) {
        <div class="flex items-center gap-3 rounded-2xl bg-white px-3 py-2.5 shadow-sm">
          @if (s.imageUrl) {
            <img [src]="s.imageUrl" alt="" class="h-10 w-10 shrink-0 rounded-lg object-cover" />
          } @else {
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#EAF5EF] text-lg">🌱</span>
          }
          <span class="min-w-0 flex-1 truncate text-sm font-medium text-[#062825]">{{ s.name }}</span>
          <span class="shrink-0 rounded-full bg-[#EAF5EF] px-2 py-0.5 text-[11px] text-[#317756]">
            {{ 'lunar.sowing.now' | translate }}
          </span>
        </div>
      }
    </div>
  }

  <!-- Gratuit : la semaine est verrouillée, on dit ce qu'elle apporte -->
  @if (!premiumService.isPremium()) {
    <div class="mt-6">
      <vw-premium-teaser
        [message]="'premium.teaser.lunar' | translate"
        [detail]="'premium.teaser.lunar-detail' | translate"
        source="lunar_week"
        (ctaClicked)="premiumPaywallOpen = true"
      ></vw-premium-teaser>
    </div>
  }

  <!-- Légende : les quatre familles, et quand revient chacune -->
  <h3 class="mt-6 text-lg font-semibold text-[#062825]">
    {{ 'lunar.legend' | translate }}
  </h3>
  <div class="mt-2 flex flex-col gap-2">
    @for (inf of influences; track inf) {
      <div class="flex items-start gap-3 rounded-2xl bg-white px-3 py-2.5 shadow-sm">
        <span class="text-lg">{{ 'lunar.influences.' + inf + '.emoji' | translate }}</span>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-[#062825]">
            {{ 'lunar.influences.' + inf + '.title' | translate }}
          </p>
          <p class="text-xs text-[#9BA6A1]">
            {{ 'lunar.influences.' + inf + '.concerns' | translate }}
          </p>
        </div>
        @if (nextDayOf(inf); as next) {
          <span class="shrink-0 self-center rounded-full bg-[#EFECFF] px-2 py-0.5 text-[11px] text-[#4C4080]">
            {{ next.weekday }} {{ next.dayNumber }}
          </span>
        }
      </div>
    }
  </div>
  }
</div>

<vw-premium-toast
  [isOpen]="premiumPaywallOpen"
  [source]="'lunar_week'"
  (close)="premiumPaywallOpen = false"
></vw-premium-toast>
